import { List, Stack, Text } from "@mantine/core";
import { useMemo } from "react";
import { useParagraph6Contracts } from "../../../modules/Paragraph6/hooks/useParagraph6Contracts";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import type { BasePeriodRow } from "../PeriodBasedTable/PeriodBasedTable.types";

interface DeleteModalContentProps {
  row?: BasePeriodRow;
  variantId?: number;
  generatorId?: number;
}

export function MarketingTypeDeleteModalContent({
  row,
  variantId,
  generatorId
}: DeleteModalContentProps) {
  const { paragraph6Contracts, isLoading, error } =
    useParagraph6Contracts(variantId);

  const affectedContracts = useMemo(() => {
    if (!paragraph6Contracts || !generatorId || !row) {
      return [];
    }

    return paragraph6Contracts
      .filter((contract) => {
        const isGeneratorIncluded = contract.gemeinde_anteile_erzeuger?.some(
          (anteil) => anteil.generator === generatorId
        );

        const isDateValid =
          !row.last_date ||
          backendDateOrDateTimeToLuxonDateTime(row.last_date) >=
            backendDateOrDateTimeToLuxonDateTime(contract.begin);

        return isGeneratorIncluded && isDateValid;
      })
      .map((contract) => contract.name);
  }, [paragraph6Contracts, generatorId, row]);

  return (
    <Stack gap="md">
      <Text>Möchten Sie diesen Eintrag löschen?</Text>
      <Text fz="sm">
        {row?.first_date} bis {row?.last_date || "heute"}
      </Text>
      <Text>
        Bitte beachten Sie, dass die Inhalte danach nicht wiederhergestellt
        werden können. Das Löschen des Eintrags könnte Auswirkungen auf
        Gutschriften (§ 6) und Erlösangaben im Erlösmonitoring haben.
      </Text>
      <LoadOrError error={error} loading={isLoading}>
        {affectedContracts.length > 0 && (
          <>
            <Text>Betroffene Verträge:</Text>
            <List>
              {affectedContracts.map((contract, index) => (
                <List.Item key={`${contract}-${index}`}>{contract}</List.Item>
              ))}
            </List>
          </>
        )}
      </LoadOrError>
    </Stack>
  );
}
